import React from 'react';
import { Box, BoxProps, Heading, useColorModeValue } from '@chakra-ui/react';
import i18next from 'i18next';
import { useAtom } from 'jotai';

import APP_CONFIG from '@/app.config';
import Icon from '@/components/0-reusable-components/icon';
import Authorized from '@/components/all-providers/authorized';
import FeatureFlag from '@/components/all-providers/feature-flag';
import { MenuItem } from '@/menu.config';
import { favoriteMenuItemKeysAtom } from '@/store/favorite-menu-items/favorite-menu-items.store';

import SideListItem from './side-list-item';

type SectionProps = { headerText: string; navItems: MenuItem[] };

const MenuSection = ({ headerText, navItems }: SectionProps): React.ReactElement => (
  <>
    <Heading key={headerText} size='sm' isTruncated px='30px' py='16px'>
      {headerText}
    </Heading>
    {navItems.map((menuItem: MenuItem) => (
      <Authorized key={menuItem.key} requiredRoles={menuItem.requiredRoles || []}>
        <FeatureFlag requiredFlags={menuItem.requiredFeatureFlags || []}>
          <SideListItem
            key={menuItem.key}
            menuItemKey={menuItem.key}
            href={menuItem.path}
            leftIcon={<Icon name={menuItem.icon || menuItem.key} />}
            primaryText={i18next.t(menuItem.label)}
          />
        </FeatureFlag>
      </Authorized>
    ))}
  </>
);

const FavoriteSection = (): React.ReactElement | null => {
  const [storedfavoriteItems] = useAtom(favoriteMenuItemKeysAtom);

  if (storedfavoriteItems.length === 0) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const navItems: MenuItem[] = [];
  for (const section of Object.keys(APP_CONFIG.menu)) {
    const sectionItems = APP_CONFIG.menu[section];
    const filteredMenuItems = sectionItems.filter((menuItem: any) =>
      storedfavoriteItems.includes(menuItem.key),
    );
    navItems.push(...filteredMenuItems);
  }

  return (
    <MenuSection
      key='favorite-menu-section'
      headerText={i18next.t('shared.favorites')}
      navItems={navItems}
    />
  );
};

const AllSections = (): React.ReactElement => (
  <>
    {Object.keys(APP_CONFIG.menu).map((section: string, index: number) => (
      <MenuSection
        key={`section_${index}`}
        headerText={i18next.t(section)}
        navItems={APP_CONFIG.menu[section]}
      />
    ))}
  </>
);

const SidebarContent = ({ ...rest }: BoxProps): React.ReactElement => {
  return (
    <Box
      transition='3s ease'
      bg={useColorModeValue('white', 'gray.900')}
      borderRight='1px'
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      h='full'
      {...rest}
    >
      <FavoriteSection />
      <AllSections />
    </Box>
  );
};

export default SidebarContent;
